import { useRef, useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUser, useAuth } from '@/contexts/UserContext';
import { getUserBadge } from '@/utils/navigation';
import { eventApi } from '@/services/eventsApi';

import HeroCarousel from '@/components/events/hero/HeroCarousel';
import FilterSection from '@/components/events/filter/FilterSection';
import EventsList from '@/components/events/main/EventsList';
import EventModal from '@/components/events/modal/EventModal';
import SuccessModal from '@/components/events/modal/SuccessModal';
import ReservationModal from '@/components/events/modal/ReservationModal';
import SubscriptionCard from '@/components/events/hero/SubscriptionCard';

import type { Event, FilterState, PaginationInfo, FilterOptions } from '@/types/event';
import './ProfessionalNetworking.css';

const ProfessionalNetworking = () => {
  const {isAuthenticated} = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const [events, setEvents] = useState<Event[]>([]);
  const [featured, setFeatured] = useState<Event[]>([]);  
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationData, setReservationData] = useState<any>(null);
  
  // Add state to track if we're loading a specific event
  const [loadingSpecificEvent, setLoadingSpecificEvent] = useState(false);
  
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    date: '',
    category: 'prof-networking',
    tag: 'all',
    location: ''
  });
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    last_page: 1,
    per_page: 6,
    total: 0,
    from: 1,
    to: 6,
    links: [],
    next_page_url: null,
    prev_page_url: null
  });
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    categories: {},
    locations: {},
    category_buttons: {},
    tag_buttons: {},
    date_filters: {}
  });
  const [loading, setLoading] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState<Record<string, boolean>>({});
  const [myQuotas, setMyQuotasStatus] = useState<Record<string, any>>({});
  
  // Move showSubscriptionDetails state to parent
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(false);

  const { user, isLoading } = useUser();
  const userBadge = user?.role ? getUserBadge(user.role) : null;
  
  const isInitialMount = useRef(true);
  const prevFilters = useRef<FilterState>(filters);

  // Function to fetch a specific event by ID
const fetchSpecificEvent = async (eventId: string, scheduleId: string | null) => {
  if (!eventId) return;
  
  setLoadingSpecificEvent(true);
  try {
    const params = new URLSearchParams({
      page: '1',
      per_page: '6',
      ...(filters.search && { search: filters.search }),
      ...(filters.date && { date_filter: filters.date }),
      ...(filters.category && { category: filters.category }),
      ...(filters.tag && { tag: filters.tag }),
      ...(filters.location && { location: filters.location }),
      eventId: eventId,
    });
    
    if (scheduleId) {
      params.set('scheduleId', scheduleId);
    }
    
    const response = await eventApi.getEvents(params as any);
    const { events: eventsData, featured: featuredData, specific_event, specific_event_id } = response;
    
    updateEvents(eventsData.data, featuredData.data, eventsData);
    
    // Find the specific event in the returned events
    let event:Event|null|undefined = null;
    if (specific_event_id) {
      event = eventsData.data.find((e: Event) => e.id.toString() === specific_event_id.toString());
    }
    
    // Fallback: find event by scheduleId
    if (!event && scheduleId) {
      event = eventsData.data.find((e: Event) => 
        e.next_schedule?.id?.toString() === scheduleId
      );
    }
    
    // Fallback: find event by eventId
    if (!event) {
      event = eventsData.data.find((e: Event) => e.id.toString() === eventId);
    }
      
      if (event) {
        setSelectedEvent(event);
        
        // Check registration status for the event
        if (user && event?.next_schedule?.id) {
          try {
            const status = await eventApi.getRegistrationStatus(event?.next_schedule?.id);
            setRegistrationStatus(prev => ({
              ...prev,
              [event.id.toString()]: status.is_registered
            }));
          } catch (error) {
            console.error('Error checking registration status:', error);
          }
        }
        
        // Open the appropriate modal based on URL action
        const action = searchParams.get('action');
        if (action === 'reservation') {
          setShowReservationModal(true);
        } else if (action === 'details' || !action) {
          setIsModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Error fetching specific event:', error);
      toast.error('Failed to load event details', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoadingSpecificEvent(false);
    }
  };

  // Effect to handle URL parameters on component mount
  useEffect(() => {
    const eventId = searchParams.get('eventId');
    const scheduleId = searchParams.get('scheduleId');
    const action = searchParams.get('action');
    
    if (eventId && (action === 'reservation' || action === 'details')) {
      // Fetch events with specific event when URL contains eventId/scheduleId
      fetchSpecificEvent(eventId, scheduleId);
    }
  }, [searchParams, user]); // Re-run when URL params or user changes

  const updateEvents = (eventsData: Event[], featuredData: Event[], paginationInfo: PaginationInfo, filtersData?: any) => {
    setEvents(eventsData ?? []);
    setFeatured(featuredData.length ? featuredData : eventsData);
    setPagination(paginationInfo);
    
    if (filtersData) {
      setFilterOptions(filtersData);
    }
  };

  const openEventModal = async (event: Event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
    
    // Check registration status when modal opens
    if (user && event?.next_schedule?.id) {
      try {
        const status = await eventApi.getRegistrationStatus(event?.next_schedule?.id);
        setRegistrationStatus(prev => ({
          ...prev,
          [event.id.toString()]: status.is_registered
        }));
      } catch (error) {
        console.error('Error checking registration status:', error);
      }
    }
  };

  const closeEventModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
    
    // Clear URL parameters when closing modal
    if (searchParams.get('eventId') || searchParams.get('scheduleId') || searchParams.get('action')) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  };

  const handleFilterChange = (newFilters: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Open reservation modal
  const handleReservationInit = (event: Event) => {
    setSelectedEvent(event);
    setShowReservationModal(true);
  };

  // Handle event registration from reservation modal
  const handleEventRegistration = async (scheduleId: string | number, phoneNumber?: string) => {
    try {
      const registrationData: any = {};
      
      if (phoneNumber) {
        registrationData.phone_number = phoneNumber;
      } else if (user?.phone_code && user?.phone) {
        registrationData.phone_number = user.phone_code + user.phone;
      }
      
      const result = await eventApi.registerForEvent(scheduleId, registrationData);

      const resultData = result.data as any;
      
      fetchMyQuotas();
      setReservationData(resultData);
      setShowReservationModal(false);
      setShowSuccessModal(true);

      const eventId = resultData?.event_schedule?.fk_event_id || resultData?.event_schedule?.event?.id;
      
      if (eventId) {
        // Update registration status
        setRegistrationStatus(prev => ({
          ...prev,
          [eventId.toString()]: true
        }));
      }

      // Refresh events to update counts
      fetchEvents(pagination.current_page, filters);
      
      return result;
    } catch (error: any) {
      const defaultError = 'Error registering for event';
      console.error(`${defaultError}:`, error);
      
      // Show error toast with response message
      const errorMessage = error.response?.error || error.response?.message || defaultError;

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      throw error;
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setReservationData(null);
    
    // Clear URL parameters when closing success modal
    if (searchParams.get('eventId') || searchParams.get('scheduleId') || searchParams.get('action')) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  };

  const handleCloseReservationModal = () => {
    setShowReservationModal(false);
    
    // Clear URL parameters when closing reservation modal
    if (searchParams.get('eventId') || searchParams.get('scheduleId') || searchParams.get('action')) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  };

  const fetchEvents = async (page: number = 1, currentFilters: FilterState = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '6',
        ...(currentFilters.search && { search: currentFilters.search }),
        ...(currentFilters.date && { date_filter: currentFilters.date }),
        ...(currentFilters.category && { category: currentFilters.category }),
        ...(currentFilters.tag && { tag: currentFilters.tag }),
        ...(currentFilters.location && { location: currentFilters.location }),
      });

      const response = await eventApi.getEvents(params as any);
      const { events: eventsData, featured: featuredData } = response;
      updateEvents(eventsData.data, featuredData.data, eventsData);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await eventApi.getFilterOptions();
      setFilterOptions(response.filters);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchMyQuotas = async () => {
    try {
      const response = await eventApi.getMyQuotas();
      const quotas = (response as any)?.data || response;
      setMyQuotasStatus(quotas);
    } catch (error) {
      console.error('Error fetching my quotas:', error);
    }
  };

  const handlePageChange = (page: number) => {
    fetchEvents(page, filters);
  };

  // Fetch user registrations on component mount
  const fetchUserRegistrations = async () => {
    if (!user) return;
    
    try {
      const response = await eventApi.getUserRegistrations();
      const statusMap: Record<string, boolean> = {};
      
      if(response.registrations) {
        response.registrations.forEach(reg => {
          statusMap[reg.event_id] = true;
        });
      }
      
      setRegistrationStatus(statusMap);
    } catch (error) {
      console.error('Error fetching user registrations:', error);
    }
  };

  useEffect(() => {
    // Initial load
    if (isInitialMount.current) {
      fetchEvents(1, filters);
      fetchUserRegistrations();
      isInitialMount.current = false;
      return;
    }

    // Only refetch when filters actually change (not on every render)
    const hasFiltersChanged = JSON.stringify(prevFilters.current) !== JSON.stringify(filters);
    
    if (hasFiltersChanged) {
      // Add a small debounce to prevent rapid API calls
      const timeoutId = setTimeout(() => {
        fetchEvents(1, filters);
      }, 300);

      prevFilters.current = { ...filters };
      
      return () => clearTimeout(timeoutId);
    }
  }, [filters]); // Only depend on filters

  // Fix 2: Separate useEffect for initial filter options
  useEffect(() => {
    fetchFilterOptions();
    fetchMyQuotas();
  }, []);

  // Refresh registrations when user changes
  useEffect(() => {
    if (user) {
      fetchUserRegistrations();
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-transparent text-white font-sans relative">
      <section className="flex flex-col sm:flex-row items-start justify-between mt-7 mx-4 md:mx-6 overflow-hidden bg-transparent">
        <div className="flex-col mx-2 md:mx-0">
          <h1 className="text-white text-2xl md:text-4xl font-bold tracking-wide">Professional Networking Event</h1>
          <p className="text-white/90 text-sm md:text-base font-normal leading-5 sm:leading-7">Connect with industry leaders and expand your professional network</p>
        </div>
        
      {/* Subscription Card */}
      {isAuthenticated && (
        <div className="sm:absolute mt-4 mx-auto sm:mt-0 sm:top-1 sm:right-6 z-40">
          <SubscriptionCard 
            quota={myQuotas}
            showDetails={showSubscriptionDetails}
            onToggleDetails={() => setShowSubscriptionDetails(!showSubscriptionDetails)}
          />
        </div>
      )}
        
        {/* Login Required Dialog */}
        {!isAuthenticated && (
          <div className="flex-col sm:top-1 sm:right-6 login-required-dialog -mx-4 sm:mx-0">
            <div className="login-dialog-content">
              <div className="w-full sm:max-w-fit flex items-center justify-center sm:justify-start">
                <img
                  src="/inform.svg"
                  alt="Information"
                  className="login-dialog-icon"
                  width="20"
                  height="20"
                />
              </div>
              <div className="login-dialog-text text-left">
                <div className="login-dialog-title">Please sign up or log in to register</div>
                <div className="login-dialog-subtitle">You need an account to register for coaching sessions.</div>
              </div>
              <Link to="/signup" className="login-dialog-signup-btn">
                Sign Up
              </Link>
            </div>
          </div>
        )}
      </section>

      {featured.length ? 
      (<HeroCarousel 
        quota={myQuotas}
        featuredEvents={featured} 
        onLearnMore={openEventModal}
      />) : (
        <div className="text-center py-4 md:py-12">
          <p className="text-gray-400 text-lg">No featured events found.</p>
        </div>)}
      
      <FilterSection 
        filters={filters}
        onFilterChange={handleFilterChange}
        filterOptions={filterOptions}
      />
      
      {loadingSpecificEvent && (
        <div className="text-center py-8">
          <p className="text-gray-400">Loading event details...</p>
        </div>
      )}
      
      <EventsList 
        events={events}
        quota={myQuotas}
        onRegister={handleReservationInit}
        onEventClick={openEventModal}
        pagination={pagination}
        onPageChange={handlePageChange}
        loading={loading}
        registrationStatus={registrationStatus as any}
      />

      {isModalOpen && selectedEvent && (
        <EventModal 
          event={selectedEvent}
          quota={myQuotas}
          onClose={closeEventModal}
          onRegister={handleReservationInit}
          isRegistered={registrationStatus[selectedEvent.id.toString()] || false}
        />
      )}
      
      {showReservationModal && selectedEvent && (
        <ReservationModal
          event={selectedEvent}
          quota={myQuotas}
          onClose={handleCloseReservationModal}
          onConfirm={handleEventRegistration}
        />
      )}
      
      {showSuccessModal && selectedEvent && (
        <SuccessModal
          event={selectedEvent}
          quota={myQuotas}
          reservationData={reservationData}
          onClose={handleCloseSuccessModal}
        />
      )}
    </div>
  );
};

export default ProfessionalNetworking;